import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { FileText, Calendar, User, Eye, Printer } from "lucide-react";
import { ALL_CREDIT_REASON_TYPES } from '@/components/customers/credits/CreateManualCreditModal';
import UniversalStatusBadge from "@/components/shared/UniversalStatusBadge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import EntityActions from "@/components/shared/EntityActions";
import { formatCurrency } from '@/lib/utils';

interface CreditWithCustomer {
  id: string;
  credit_amount: number;
  remaining_amount: number;
  source_type: string;
  reason_type: string | null;
  source_invoice_id: string | null;
  description: string | null;
  status: string;
  credit_note_number: string | null;
  credit_note_issued_at: string | null;
  created_at: string;
  customer: {
    id: string;
    name: string;
    last_name: string;
  } | null;
  source_invoice: {
    invoice_number: string | null;
  } | null;
}

interface CreditNoteCardProps {
  credits: CreditWithCustomer[];
  onPreview: (creditId: string) => void;
}

/**
 * CreditNoteCard - Card/grid view for credit notes list
 * 
 * Displays credit notes as visual cards showing:
 * - Credit note number
 * - Status badge
 * - Customer name
 * - Credit amount and remaining
 * - Source information
 * - Actions menu
 */
const CreditNoteCard: React.FC<CreditNoteCardProps> = ({ credits, onPreview }) => {
  const navigate = useNavigate();


  const renderStatusBadge = (status: string) => (
    <UniversalStatusBadge entityType="credit" status={status} size="sm" />
  );

  const getCreditActions = (credit: CreditWithCustomer) => [
    {
      label: "View Details",
      icon: Eye,
      onClick: () => navigate(`/credits/${credit.id}`),
    },
    {
      label: "View/Print Credit Note",
      icon: Printer,
      onClick: () => onPreview(credit.id),
      hidden: !credit.credit_note_number,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {credits.map((credit) => (
          <Card 
            key={credit.id} 
            className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
          onClick={() => navigate(`/credits/${credit.id}`)}
        >
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  {credit.credit_note_number ? (
                    <span className="font-semibold font-mono">
                      {credit.credit_note_number}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Not issued
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {renderStatusBadge(credit.status)}
                <EntityActions actions={getCreditActions(credit)} size="sm" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Customer */}
            {credit.customer && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <User className="h-4 w-4" />
                <Link
                  to={`/customers?selected=${credit.customer.id}`}
                  className="text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {credit.customer.name} {credit.customer.last_name}
                </Link>
              </div>
            )}
            
            {/* Amounts */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-lg font-semibold">
                  {formatCurrency(credit.credit_amount)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Remaining: {formatCurrency(credit.remaining_amount)}
                </div>
              </div>
            </div>
            
            {/* Source and Date */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground min-w-0">
              <div className="truncate min-w-0 max-w-full">
                Source: {credit.source_type === 'overpayment' && credit.source_invoice?.invoice_number
                  ? `Overpayment - ${credit.source_invoice.invoice_number}`
                  : credit.source_type === 'manual'
                    ? `Manual${credit.reason_type ? ` - ${ALL_CREDIT_REASON_TYPES.find(t => t.value === credit.reason_type)?.label || credit.reason_type}` : ''}`
                    : credit.source_type}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Calendar className="h-4 w-4" />
                {format(new Date(credit.created_at), 'dd MMMM yyyy')}
              </div>
            </div>

          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default CreditNoteCard;
